/* eslint-disable camelcase */
import {
  CertificateRadio,
  CertificateType,
  CertificateValueBorn,
  CertificateValueCitizenship,
  CertificateValueCivilUnion,
  CertificateValueDeath,
  CertificateValueMarriage,
  CertificationFormVM,
} from 'civil-status/view-models/document-birth-form';
import { DateTime } from 'luxon';
import { DESCRIPTION_ITALIAN_NATION, ID_ITALIAN_NATION } from 'shell/constants/id-italian-nation';
import { CertificateRequestAnsc, RequesterCertificate } from 'shell/models/certificate-ansc';
import { SubjectData } from 'shell/models/person';
// import { SubjectData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';

const transformCertificateType = (certificate?: CertificateRadio | null): string => {
  let certificateType = 'ASC';

  const isCopia =
    certificate?.element?.key?.codiceDocumento === CertificateValueBorn.copia ||
    certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.copia ||
    certificate?.element?.key?.codiceDocumento === CertificateValueDeath.copia ||
    certificate?.element?.key?.codiceDocumento === CertificateValueCivilUnion.copia ||
    certificate?.element?.key?.codiceDocumento === CertificateValueCitizenship.copia;

  if (isCopia) {
    certificateType = `${certificateType}COPIACNF`;
    return certificateType;
  }

  const isCertificato =
    certificate?.element?.key?.codiceDocumento === CertificateValueBorn.certificato ||
    certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.certificato ||
    certificate?.element?.key?.codiceDocumento === CertificateValueDeath.certificato ||
    certificate?.element?.key?.codiceDocumento === CertificateValueCivilUnion.certificato ||
    certificate?.element?.key?.codiceDocumento === CertificateValueCitizenship.certificato;

  if (isCertificato) {
    certificateType = `${certificateType}CRT`;
  }

  const isEstratto =
    certificate?.element?.key?.codiceDocumento === CertificateValueBorn.estratto ||
    certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.estratto ||
    certificate?.element?.key?.codiceDocumento === CertificateValueDeath.estratto ||
    certificate?.element?.key?.codiceDocumento === CertificateValueCivilUnion.estratto ||
    certificate?.element?.key?.codiceDocumento === CertificateValueCitizenship.estratto;

  if (isEstratto) {
    certificateType = `${certificateType}ESN`;
  }

  if (certificate?.type === CertificateType.nascita) {
    certificateType = `${certificateType}NASC`;
  }

  if (certificate?.type === CertificateType.morte) {
    certificateType = `${certificateType}MRT`;
  }

  if (certificate?.type === CertificateType.matrimonio) {
    certificateType = `${certificateType}MATR`;
  }

  if (certificate?.type === CertificateType.unioneCivile) {
    certificateType = `${certificateType}UCIV`;
  }

  if (certificate?.type === CertificateType.cittadinanza) {
    certificateType = `${certificateType}CITT`;
  }

  const isEstrattoParigi =
    certificate?.element?.key?.codiceDocumento === CertificateValueBorn.estrattoParigi ||
    certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.estrattoParigi ||
    certificate?.element?.key?.codiceDocumento === CertificateValueDeath.estrattoParigi;

  if (isEstrattoParigi) {
    certificateType = certificateType.replace('ASC', 'ASCESI');
    certificateType = `${certificateType}P`;
  }

  const isEstrattoVienna =
    certificate?.element?.key?.codiceDocumento === CertificateValueBorn.estrattoVienna ||
    certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.estrattoVienna ||
    certificate?.element?.key?.codiceDocumento === CertificateValueDeath.estrattoVienna;

  if (isEstrattoVienna) {
    certificateType = certificateType.replace('ASC', 'ASCESI');
    certificateType = `${certificateType}V`;
  }

  return certificateType;
};

const transformExtract = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  registryItem: ItemVM | undefined
) => {
  const [year, numberRegister, numberAct, portion, serie] =
    values.chosenDocument?.actNumber?.split('-') || [];

  const isEstratto =
    values.certificate?.element?.key?.codiceDocumento === CertificateValueBorn.estratto ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.estratto ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueDeath.estratto ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueCivilUnion.estratto ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueCitizenship.estratto;

  const isEstrattoParigi =
    values.certificate?.element?.key?.codiceDocumento === CertificateValueBorn.estrattoParigi ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.estrattoParigi ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueDeath.estrattoParigi;

  const isEstrattoVienna =
    values.certificate?.element?.key?.codiceDocumento === CertificateValueBorn.estrattoVienna ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.estrattoVienna ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueDeath.estrattoVienna;

  if (isEstratto || isEstrattoParigi || isEstrattoVienna) {
    let extract: Record<string, unknown> = {
      usoInterno: values.flagInternal ? '1' : '0',
      numeroCopie: values.copies,
      cartaLibera: values.flagCarta ? '1' : '0',
      firmaOmessa: values.flagOmittedSign ? '1' : '0',
    };

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.nascita) {
      extract.nascitaEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'NASCITA',
          },
          definizioneAtto: {
            anno: year,
            numero: numberAct,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion,
            serie: serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenBirth?.surname || '',
            nome: values.documentChosenBirth?.name || '',
            sesso: values.documentChosenBirth?.gender || '',
          },
          datiPadre: {
            cognome: values.documentChosenBirth?.surnameFather || '',
            nome: values.documentChosenBirth?.nameFather || '',
          },
          datiMadre: {
            cognome: values.documentChosenBirth?.surnameMother || '',
            nome: values.documentChosenBirth?.nameMother || '',
          },
          definizioneNascita: {
            luogoNascita: values.documentChosenBirth?.municipalityBirth || '',
            dataNascita: values.documentChosenBirth?.birthday
              ? values.documentChosenBirth?.birthday?.toFormat('dd/MM/yyyy')
              : '',
            oraNascita: values.documentChosenBirth?.time
              ? values.documentChosenBirth?.time?.toFormat('HH:mm')
              : '',
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoNascitaVO: extract };
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.morte) {
      extract.morteEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MORTE',
          },
          definizioneAtto: {
            anno: year,
            numero: numberAct,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion,
            serie: serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenDeath?.surname || '',
            nome: values.documentChosenDeath?.name || '',
            sesso: values.documentChosenDeath?.gender || '',
            statoCivile: values.documentChosenDeath?.status,
            residenza: values.documentChosenDeath?.residenceMunicipality,
            coniuge: `${values.documentChosenDeath?.spouseSurname || ''} ${
              values.documentChosenDeath?.spouseName || ''
            }`,
            nomeConiuge: values.documentChosenDeath?.spouseName,
            cognomeConiuge: values.documentChosenDeath?.spouseSurname,
            nascita: {
              luogo: values.documentChosenDeath?.municipalityBirth,
              data: values.documentChosenDeath?.birthday
                ? values.documentChosenDeath?.birthday?.toFormat('dd/MM/yyyy')
                : '',
              eta: values.documentChosenDeath?.years,
              anno: values.documentChosenDeath?.yearDate,
            },
          },
          datiMorte: {
            luogo: values.documentChosenDeath?.deathMunicipality,
            dataMorteCerta:
              values?.documentChosenDeath?.deathPresumed || !values.documentChosenDeath?.deathDate
                ? ''
                : values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy'),
            dataMortePresuntaDal: values.documentChosenDeath?.deathBy
              ? values.documentChosenDeath?.deathBy?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
            dataMortePresuntaAl: values.documentChosenDeath?.deathTo
              ? values.documentChosenDeath?.deathTo?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
            orarioMorte: values?.documentChosenDeath?.hourDeathUnknown
              ? 'imprecisate'
              : values?.documentChosenDeath?.hourDeathDate
              ? values.documentChosenDeath.hourDeathDate.toFormat('HH:mm')
              : ' ',
          },
          // datiPadre: {
          //   cognome: values.documentChosenDeath?.surnameFather || '',
          //   nome: values.documentChosenDeath?.nameFather || '',
          // },
          // datiMadre: {
          //   cognome: values.documentChosenDeath?.surnameMother || '',
          //   nome: values.documentChosenDeath?.nameMother || '',
          // },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoMorteVO: extract };
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.matrimonio) {
      extract.matrimonioEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MATRIMONIO',
          },
          definizioneAtto: {
            anno: year,
            numero: numberAct,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion,
            serie: serie || ' ',
          },
          coniugeUno: {
            cognome: values.documentChosenMarriage?.surnameHusband,
            nome: values.documentChosenMarriage?.nameHusband,
            sesso: values.documentChosenMarriage?.genderHusband,
            cittadinanza: values.documentChosenMarriage?.citizenshipHusband,
            statoCivile: values.documentChosenMarriage?.statusHusband,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthHusband,
              data: values.documentChosenMarriage?.birthdayHusband
                ? values.documentChosenMarriage?.birthdayHusband?.toFormat('dd/MM/yyyy')
                : '',
              eta: values.documentChosenMarriage?.yearsHusband,
              anno: values.documentChosenMarriage?.yearDateHusband,
            },
            residenza: values.documentChosenMarriage?.residenceMunicipalityHusband,
          },
          coniugeDue: {
            cognome: values.documentChosenMarriage?.surnameWife,
            nome: values.documentChosenMarriage?.nameWife,
            sesso: values.documentChosenMarriage?.genderWife,
            statoCivile: values.documentChosenMarriage?.statusWife,
            cittadinanza: values.documentChosenMarriage?.citizenshipWife,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthWife,
              data: values.documentChosenMarriage?.birthdayWife
                ? values.documentChosenMarriage?.birthdayWife?.toFormat('dd/MM/yyyy')
                : '',
              eta: values.documentChosenMarriage?.yearsWife,
              anno: values.documentChosenMarriage?.yearDateWife,
            },
            residenza: values.documentChosenMarriage?.residenceMunicipalityWife,
          },
          datiMatrimonio: {
            data: values.documentChosenMarriage?.marriageDate
              ? values.documentChosenMarriage?.marriageDate?.toFormat('dd/MM/yyyy')
              : '',
            luogo: values.documentChosenMarriage?.marriageMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoMatrimonioVO: extract };
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.unioneCivile) {
      extract.unioneCivileEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'UNIONE CIVILE',
          },
          definizioneAtto: {
            anno: year || undefined,
            numero: numberAct || undefined,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion || undefined,
            serie: serie || ' ',
          },
          dichiaranteUno: {
            cognome: values.documentChosenCivilUnion?.surname || undefined,
            nome: values.documentChosenCivilUnion?.name || undefined,
            sesso: values.documentChosenCivilUnion?.gender || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth || undefined,
              data: values.documentChosenCivilUnion?.birthday
                ? values.documentChosenCivilUnion?.birthday.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant1,
              anno: values.documentChosenCivilUnion?.yearsDeclarant1 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant1,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant1,
          },
          dichiaranteDue: {
            cognome: values.documentChosenCivilUnion?.surname2 || undefined,
            nome: values.documentChosenCivilUnion?.name2 || undefined,
            sesso: values.documentChosenCivilUnion?.gender2 || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth2 || undefined,
              data: values.documentChosenCivilUnion?.birthday2
                ? values.documentChosenCivilUnion?.birthday2.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant2,
              anno: values.documentChosenCivilUnion?.yearsDeclarant2 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant2,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant2,
          },
          datiUnione: {
            data: values.documentChosenCivilUnion?.civilUnionDate
              ? values.documentChosenCivilUnion?.civilUnionDate?.toFormat('dd/MM/yyyy')
              : '',
            luogo: values.documentChosenCivilUnion?.civilUnionMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoUnioneCivileVO: extract };
    }

    return extract;
  }

  return {};
};

const transformCertification = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  registryItem: ItemVM | undefined
) => {
  const [year, numberRegister, numberAct, portion, serie] =
    values.chosenDocument?.actNumber?.split('-') || [];

  const isCertificato =
    values.certificate?.element?.key?.codiceDocumento === CertificateValueBorn.certificato ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.certificato ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueDeath.certificato ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueCivilUnion.certificato ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueCitizenship.certificato;

  if (isCertificato) {
    let certification: Record<string, unknown> = {
      usoInterno: values.flagInternal ? '1' : '0',
      numeroCopie: values.copies,
      firmaOmessa: values.flagOmittedSign ? '1' : '0',
      cartaLibera: values.flagCarta ? '1' : '0',
    };

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.nascita) {
      certification.nascitaCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'NASCITA',
          },
          definizioneAtto: {
            anno: year,
            numero: numberAct,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion,
            serie: serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenBirth?.surname || '',
            nome: values.documentChosenBirth?.name || '',
            sesso: values.documentChosenBirth?.gender || '',
          },
          datiPadre: {
            cognome: values.documentChosenBirth?.surnameFather || '',
            nome: values.documentChosenBirth?.nameFather || '',
          },
          datiMadre: {
            cognome: values.documentChosenBirth?.surnameMother || '',
            nome: values.documentChosenBirth?.nameMother || '',
          },
          definizioneNascita: {
            luogoNascita: values.documentChosenBirth?.municipalityBirth || '',
            dataNascita: values.documentChosenBirth?.birthday
              ? values.documentChosenBirth?.birthday?.toFormat('dd/MM/yyyy')
              : '',
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoNascitaVO: certification };
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.morte) {
      certification.morteCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MORTE',
          },
          definizioneAtto: {
            anno: year,
            numero: numberAct,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion,
            serie: serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenDeath?.surname || '',
            nome: values.documentChosenDeath?.name || '',
            sesso: values.documentChosenDeath?.gender || '',
            statoCivile: values.documentChosenDeath?.status,
            residenza: values.documentChosenDeath?.residenceMunicipality,
            coniuge: `${values.documentChosenDeath?.spouseSurname || ''} ${
              values.documentChosenDeath?.spouseName || ''
            }`,
            nascita: {
              luogo: values.documentChosenDeath?.municipalityBirth,
              data: values.documentChosenDeath?.birthday
                ? values.documentChosenDeath?.birthday?.toFormat('dd/MM/yyyy')
                : '',
              eta: values.documentChosenDeath?.years,
              anno: values.documentChosenDeath?.yearDate,
            },
          },
          datiMorte: {
            luogo: values.documentChosenDeath?.deathMunicipality,
            dataMorteCerta:
              values?.documentChosenDeath?.deathPresumed || !values.documentChosenDeath?.deathDate
                ? ''
                : values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy'),
            dataMortePresuntaDal: values.documentChosenDeath?.deathBy
              ? values.documentChosenDeath?.deathBy?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
            dataMortePresuntaAl: values.documentChosenDeath?.deathTo
              ? values.documentChosenDeath?.deathTo?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
          },
          // datiPadre: {
          //   cognome: values.documentChosenDeath?.surnameFather || '',
          //   nome: values.documentChosenDeath?.nameFather || '',
          // },
          // datiMadre: {
          //   cognome: values.documentChosenDeath?.surnameMother || '',
          //   nome: values.documentChosenDeath?.nameMother || '',
          // },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoMorteVO: certification };
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.matrimonio) {
      certification.matrimonioCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MATRIMONIO',
          },
          definizioneAtto: {
            anno: year,
            numero: numberAct,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion,
            serie: serie || ' ',
          },
          coniugeUno: {
            cognome: values.documentChosenMarriage?.surnameHusband,
            nome: values.documentChosenMarriage?.nameHusband,
            sesso: values.documentChosenMarriage?.genderHusband,
            statoCivile: values.documentChosenMarriage?.statusHusband,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthHusband,
              data: values.documentChosenMarriage?.birthdayHusband
                ? values.documentChosenMarriage?.birthdayHusband?.toFormat('dd/MM/yyyy')
                : '',
              eta: values.documentChosenMarriage?.yearsHusband,
              anno: values.documentChosenMarriage?.yearDateHusband,
            },
            cittadinanza: values.documentChosenMarriage?.citizenshipHusband,
            residenza: values.documentChosenMarriage?.residenceMunicipalityHusband,
          },
          coniugeDue: {
            cognome: values.documentChosenMarriage?.surnameWife,
            nome: values.documentChosenMarriage?.nameWife,
            sesso: values.documentChosenMarriage?.genderWife,
            cittadinanza: values.documentChosenMarriage?.citizenshipWife,
            statoCivile: values.documentChosenMarriage?.statusWife,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthWife,
              data: values.documentChosenMarriage?.birthdayWife
                ? values.documentChosenMarriage?.birthdayWife?.toFormat('dd/MM/yyyy')
                : '',
              eta: values.documentChosenMarriage?.yearsWife,
              anno: values.documentChosenMarriage?.yearDateWife,
            },
            residenza: values.documentChosenMarriage?.residenceMunicipalityWife,
          },
          datiMatrimonio: {
            data: values.documentChosenMarriage?.marriageDate
              ? values.documentChosenMarriage?.marriageDate?.toFormat('dd/MM/yyyy')
              : '',
            luogo: values.documentChosenMarriage?.marriageMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoMatrimonioVO: certification };
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.unioneCivile) {
      certification.unioneCivileCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'UNIONE CIVILE',
          },
          definizioneAtto: {
            anno: year || undefined,
            numero: numberAct || undefined,
            registro: numberRegister || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: portion || undefined,
            serie: serie || ' ',
          },
          dichiaranteUno: {
            cognome: values.documentChosenCivilUnion?.surname || undefined,
            nome: values.documentChosenCivilUnion?.name || undefined,
            sesso: values.documentChosenCivilUnion?.gender || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth || undefined,
              data: values.documentChosenCivilUnion?.birthday
                ? values.documentChosenCivilUnion?.birthday.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant1,
              anno: values.documentChosenCivilUnion?.yearsDeclarant1 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant1,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant1,
          },
          dichiaranteDue: {
            cognome: values.documentChosenCivilUnion?.surname2 || undefined,
            nome: values.documentChosenCivilUnion?.name2 || undefined,
            sesso: values.documentChosenCivilUnion?.gender2 || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth2 || undefined,
              data: values.documentChosenCivilUnion?.birthday2
                ? values.documentChosenCivilUnion?.birthday2.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant2,
              anno: values.documentChosenCivilUnion?.yearsDeclarant2 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant2,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant2,
          },
          datiUnione: {
            data: values.documentChosenCivilUnion?.civilUnionDate
              ? values.documentChosenCivilUnion?.civilUnionDate?.toFormat('dd/MM/yyyy')
              : '',
            luogo: values.documentChosenCivilUnion?.civilUnionMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoUnioneCivileVO: certification };
    }

    return certification;
  }

  return {};
};

const transformCopy = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  registryItem: ItemVM | undefined
) => {
  const [year, numberRegister, numberAct, portion, serie] =
    values.chosenDocument?.actNumber?.split('-') || [];

  const isCopia =
    values.certificate?.element?.key?.codiceDocumento === CertificateValueBorn.copia ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueMarriage.copia ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueDeath.copia ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueCivilUnion.copia ||
    values.certificate?.element?.key?.codiceDocumento === CertificateValueCitizenship.copia;

  if (isCopia) {
    const copy: Record<string, unknown> = {
      usoInterno: values.flagInternal ? '1' : '0',
      numeroCopie: values.copies,
      firmaOmessa: values.flagOmittedSign ? '1' : '0',
      cartaLibera: values.flagCarta ? '1' : '0',
    };

    let codici: Array<Record<string, unknown>> = [];

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.nascita) {
      copy.immagini = (values.documentChosenBirth?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenBirth?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.morte) {
      copy.immagini = (values.documentChosenDeath?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenDeath?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.matrimonio) {
      copy.immagini = (values.documentChosenMarriage?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenMarriage?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.unioneCivile) {
      copy.immagini = (values.documentChosenCivilUnion?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenCivilUnion?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.chosenDocument?.element?.codiceAtto === CertificateType.cittadinanza) {
      copy.immagini = (values.documentChosenCitizenship?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenCitizenship?.uploadFile?.file?.name,
        },
      ];
    }

    copy.copiaConformeElement = [
      {
        cartaBianca: values.flagFincatura || false,
        intestazione: '',
        codici,
        definizioneAtto: {
          anno: year,
          numero: numberAct,
          registro: numberRegister || undefined,
          descrizioneRegistro: registryItem?.value,
          parte: portion,
          serie: serie || ' ',
        },
        timbri: {
          timbroFisso: values.timber || '',
          timbroLibero: values.reason || '',
          uso: values.flagInternal ? '1' : '0',
        },
        pieDiPagina: {
          luogoStampa: '',
          dataStampa: '',
          operatore: nomeOperatore,
          etichettaOperatore: '',
          notaFirma: '',
          tipoRilascio: '',
          diritti: '',
          numCertificato: '',
        },
      },
    ];

    return { stampaCopiaConformeVO: copy };
  }

  return {};
};

const transformUtilsRegistroDocRilasciatiVO = (
  values: CertificationFormVM,
  codiceServizioOperatore: string
): Record<string, unknown> => {
  const hasDatiSoggetto = !!values.applicant?.datiSoggetto;
  let datiSoggetto: SubjectData | undefined = undefined;

  if (hasDatiSoggetto) {
    let codiceFiscale;

    let comune;

    let localita;

    let cartaIdentita;

    if (values.applicant?.fiscalCode) {
      codiceFiscale = values.applicant?.fiscalCode
        ? {
            codFiscale: values.applicant?.fiscalCode,
          }
        : undefined;
    } else if (values.applicant?.datiSoggetto?.generalita?.codiceFiscale) {
      codiceFiscale = values.applicant?.datiSoggetto?.generalita?.codiceFiscale;
    }

    if (!values.applicant?.isForeign) {
      if (values.applicant?.municipality?.id) {
        comune = {
          codiceIstat: values.applicant?.municipality.id || '',
          nomeComune: values.applicant?.municipality?.description,
          siglaProvinciaIstat: values.applicant?.municipality?.province,
        };
      } else if (values.applicant?.datiSoggetto?.generalita?.luogoNascita?.comune) {
        comune = values.applicant?.datiSoggetto?.generalita?.luogoNascita?.comune;
      }
    } else {
      if (values.applicant?.foreignMunicipality?.code && values.applicant?.foreignState?.code) {
        localita = {
          codiceStato: values.applicant?.foreignState?.code,
          descrizioneStato: values.applicant?.foreignState?.value,
          descrizioneLocalita: values.applicant?.foreignMunicipality?.description,
          provinciaContea: values.applicant?.foreignState?.province,
        };
      } else if (values.applicant?.datiSoggetto?.generalita?.luogoNascita?.localita) {
        localita = values.applicant?.datiSoggetto?.generalita?.luogoNascita?.localita;
      }
    }

    if (
      values.applicant?.municipalityDocument?.id ||
      values.applicant?.documentNumber ||
      values.applicant?.documentIssuedDate
    ) {
      cartaIdentita = {
        comuneRilascio: values.applicant?.municipalityDocument?.id
          ? {
              codiceIstat: values.applicant?.municipalityDocument?.id,
              nomeComune: values.applicant?.municipalityDocument?.description,
              siglaProvinciaIstat: values.applicant?.municipalityDocument?.province,
            }
          : undefined,
        numero: values.applicant?.documentNumber || undefined,
        dataRilascio: values.applicant?.documentIssuedDate?.toFormat('yyyy-MM-dd') || undefined,
      };
    } else if (values.applicant?.datiSoggetto?.cartaIdentita) {
      cartaIdentita = values.applicant?.isForeignDocument
        ? undefined
        : (values.applicant?.datiSoggetto || {}).cartaIdentita;
    }

    const withGenerality =
      values.applicant?.surname ||
      values.applicant?.datiSoggetto?.generalita ||
      codiceFiscale ||
      values.applicant?.name ||
      values.applicant?.datiSoggetto?.generalita?.nome ||
      values.applicant?.birthDate ||
      values.applicant?.datiSoggetto?.generalita ||
      values.applicant?.gender ||
      values.applicant?.datiSoggetto?.generalita?.sesso ||
      comune ||
      localita;

    if (!cartaIdentita && !values.applicant?.datiSoggetto && !withGenerality) {
      datiSoggetto = undefined;
    }

    datiSoggetto = {
      ...(values.applicant?.datiSoggetto || {}),
      generalita:
        values.applicant?.surname ||
        values.applicant?.datiSoggetto?.generalita ||
        codiceFiscale ||
        values.applicant?.name ||
        values.applicant?.datiSoggetto?.generalita?.nome ||
        values.applicant?.birthDate ||
        values.applicant?.datiSoggetto?.generalita ||
        values.applicant?.gender ||
        values.applicant?.datiSoggetto?.generalita?.sesso ||
        comune ||
        localita
          ? {
              codiceFiscale,
              cognome:
                values.applicant?.surname || values.applicant?.datiSoggetto?.generalita?.cognome,
              nome: values.applicant?.name || values.applicant?.datiSoggetto?.generalita?.nome,
              dataNascita:
                values.applicant?.birthDate?.toFormat('yyyy-MM-dd') ||
                values.applicant?.datiSoggetto?.generalita?.dataNascita,
              sesso: values.applicant?.gender || values.applicant?.datiSoggetto?.generalita?.sesso,
              luogoNascita:
                comune || localita
                  ? {
                      comune,
                      localita,
                    }
                  : undefined,
            }
          : undefined,
      cartaIdentita,
    };
  } else {
    const withGenerality =
      values.applicant?.fiscalCode ||
      values.applicant?.surname ||
      values.applicant?.name ||
      values.applicant?.birthDate ||
      (!values.applicant?.isForeign && values.applicant?.municipality?.id) ||
      (values.applicant?.isForeign &&
        values.applicant?.foreignMunicipality?.code &&
        values.applicant?.foreignState?.id) ||
      values.applicant?.gender;

    const withCard =
      values.applicant?.municipalityDocument?.id ||
      values.applicant?.documentNumber ||
      values.applicant?.documentIssuedDate;

    if (!withCard && !withGenerality) {
      datiSoggetto = undefined;
    } else {
      datiSoggetto = {
        generalita: withGenerality
          ? {
              codiceFiscale: values.applicant?.fiscalCode
                ? {
                    codFiscale: values.applicant?.fiscalCode,
                  }
                : undefined,
              cognome: values.applicant?.surname,
              nome: values.applicant?.name,
              dataNascita: values.applicant?.birthDate?.toFormat('yyyy-MM-dd'),
              luogoNascita: {
                comune:
                  !values.applicant?.isForeign && values.applicant?.municipality?.id
                    ? {
                        codiceIstat: values.applicant?.municipality.id || '',
                        nomeComune: values.applicant?.municipality.description,
                        siglaProvinciaIstat: values.applicant?.municipality.province,
                      }
                    : undefined,
                localita:
                  values.applicant?.isForeign &&
                  values.applicant?.foreignMunicipality?.code &&
                  values.applicant?.foreignState?.id
                    ? {
                        codiceStato: values.applicant?.foreignState?.id,
                        descrizioneStato: values.applicant?.foreignState?.value,
                        descrizioneLocalita: values.applicant?.foreignMunicipality?.description,
                        provinciaContea: values.applicant?.foreignState?.province,
                      }
                    : undefined,
              },
              sesso: values.applicant?.gender,
            }
          : undefined,
        cartaIdentita: withCard
          ? {
              comuneRilascio: values.applicant?.municipalityDocument?.id
                ? {
                    codiceIstat: values.applicant?.municipalityDocument?.id,
                    nomeComune: values.applicant?.municipalityDocument?.description,
                    siglaProvinciaIstat: values.applicant?.municipalityDocument?.province,
                  }
                : undefined,
              numero: values.applicant?.documentNumber || undefined,
              dataRilascio:
                values.applicant?.documentIssuedDate?.toFormat('yyyy-MM-dd') || undefined,
            }
          : undefined,
      };
    }
  }

  return {
    codiceFiscaleRichiedente: values.applicant?.fiscalCode || undefined,
    idAnprRichiedente: values.applicant?.idSubjectAnpr || undefined,
    codiceModificatore: values.modifierCode,
    codiceServizioOperatore,
    //numeroRicevutaPos, // int TODO in attesa di specifiche
    datiSoggetto: datiSoggetto as SubjectData,
    motivoRilascioRichiedente: values.accountholder?.reasonRelease,
    docRiconoscimentoRichiedente: values.applicant?.isForeignDocument
      ? {
          estDocRiconoscimento: values.applicant?.foreignStateDocument || undefined,
          dataRilascio: values.applicant?.documentIssuedDate
            ? values.applicant?.documentIssuedDate.toFormat('yyyy-MM-dd')
            : undefined,
        }
      : undefined,
  };
};

export const transformCertificate = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  codiceServizioOperatore: string,
  registryItem: ItemVM | undefined
): Record<string, unknown> => {
  const tipoStampa = transformCertificateType(values.certificate);

  const extract = transformExtract(values, isNotFlagSign, nomeOperatore, registryItem);

  const certification = transformCertification(values, isNotFlagSign, nomeOperatore, registryItem);

  const copy = transformCopy(values, isNotFlagSign, nomeOperatore, registryItem);

  const utilsRegistroDocRilasciatiVO = transformUtilsRegistroDocRilasciatiVO(
    values,
    codiceServizioOperatore
  );

  return <const>{
    tipoStampa,
    tipoDocumento: values.certificate?.element?.key?.codiceDocumento || undefined,
    tipoPagamento: values.typePayment || undefined,
    utilsRegistroDocRilasciatiVO,
    ...extract,
    ...certification,
    ...copy,
  };
};

export const transformCertificateAnsc = (values: CertificationFormVM): CertificateRequestAnsc => {
  let intestatario: RequesterCertificate | null = null;

  let richiedente: RequesterCertificate | null = null;

  const hasAccountholder =
    values.accountholder?.surname ||
    values.accountholder?.name ||
    values.accountholder?.gender ||
    values.accountholder?.birthDate ||
    values.accountholder?.foreignState?.value ||
    values.accountholder?.municipality?.description;

  // valorizzazione intestatario se presente
  if (hasAccountholder) {
    const isForeign = values.accountholder?.isForeign;

    intestatario = {
      idsoggettoanpr: values.accountholder?.idSubjectAnpr,
      codiceFiscale: values.accountholder?.fiscalCode,
      cognome: values.accountholder?.surname,
      nome: values.accountholder?.name,
      sesso: values.accountholder?.gender,
      dataNascita: values.accountholder?.birthDate?.toFormat('yyyy-MM-dd'),
      idFormatodata: values.accountholder?.idTypeDate,
      formatodata: values.accountholder?.typeDate,
      anni: values.accountholder?.age,
      id: values.accountholder?.idAnsc || undefined,
      idComuneNascita: isForeign
        ? values.accountholder?.foreignMunicipality?.id
        : values.accountholder?.municipality?.id,
      idStatoNascita: isForeign
        ? values.accountholder?.foreignState?.id
        : ID_ITALIAN_NATION?.toString(),
      nomeStatoNascita: isForeign
        ? values.accountholder?.foreignState?.value
        : DESCRIPTION_ITALIAN_NATION,
    };
  }

  const hasApplicant =
    values.applicant?.surname ||
    values.applicant?.name ||
    values.applicant?.gender ||
    values.applicant?.birthDate ||
    values.applicant?.municipality?.id ||
    values.applicant?.foreignState?.id;

  if (hasApplicant) {
    const isForeign = values.applicant?.isForeign;

    richiedente = {
      idsoggettoanpr: values.applicant?.idSubjectAnpr,
      codiceFiscale: values.applicant?.fiscalCode,
      cognome: values.applicant?.surname,
      nome: values.applicant?.name,
      sesso: values.applicant?.gender,
      dataNascita: values.applicant?.birthDate?.toFormat('yyyy-MM-dd'),
      idComuneNascita: isForeign
        ? values.applicant?.foreignMunicipality?.id
        : values.applicant?.municipality?.id || values.applicant?.municipality?.code?.toString(),
      idStatoNascita: isForeign
        ? values.applicant?.foreignState?.id
        : ID_ITALIAN_NATION?.toString(),
      nomeStatoNascita: isForeign
        ? values.applicant?.foreignState?.value
        : DESCRIPTION_ITALIAN_NATION,
    };
  }

  // Valorizzazione richiedente
  if (values.accountholder?.sameAsApplicant) {
    intestatario = richiedente;
  }

  const data = transformUtilsRegistroDocRilasciatiVO(values, '');

  return {
    usoInterno: values.flagInternal ? '1' : '0',
    tipoDocumento: values.certificate?.element?.key?.codiceDocumento,
    tipoPagamento: values.typePayment || undefined,
    datiSoggetto: data?.datiSoggetto as SubjectData,
    motivoRilascioRichiedente: values.accountholder?.reasonRelease || undefined,
    codiceModificatore: values.modifierCode || undefined,
    modalitaRilascio: values.releaseMode?.id?.toString() || 'ZERO',
    numCopie: values.copies ? Number.parseInt(values.copies, 10) : 1,
    sigla: values.sigla || undefined,
    enunciazione: values.annunciazioniAtto || undefined,
    docRiconoscimentoRichiedente:
      values.applicant?.isForeignDocument &&
      (values.applicant?.foreignStateDocument || values.applicant?.documentIssuedDate)
        ? {
            estDocRiconoscimento: values.applicant?.foreignStateDocument || undefined,
            dataRilascio: values.applicant?.documentIssuedDate
              ? values.applicant?.documentIssuedDate.toFormat('yyyy-MM-dd')
              : undefined,
          }
        : undefined,
    certificatoRequest: {
      idAnsc: values.chosenDocument?.actNumber,
      idTipoCertificato: values.certificate?.element?.key?.codiceDocumento,
      idTipoEvento: values.certificate?.type,
      intestatario,
      richiedente,
      conPaternitaMaternita: values.flagPaternita || false,
      usoMatrimonio: values.flagUsePublication || false,
      idTipoUso: values.typeUse || undefined,
      parametriFirma: values.user
        ? {
            idTipoFirma: '1', // fixed
            idModalitaInput: '2', // fixed
            inputFirma1: values.user ? Buffer.from(values.user).toString('base64') : undefined,
            inputFirma2: values.pin ? Buffer.from(values.pin).toString('base64') : undefined,
            inputFirma3: values.otp ? Buffer.from(values.otp).toString('base64') : undefined,
          }
        : undefined,
    },
  };
};
